import { useEffect, useMemo, useState } from "react";

import SemverService from "@services/semverService/semverService";

import { ReleaseFilesState } from "./useReleaseFiles";

const enrollmentReleaseChannels = new Set<API.ReleaseFile["channel"]>(["beta", "live"]);

interface SelectOption {
    label: string;
    value: string;
}

interface UseProductVersionSelectionOptions {
    autoSelect?: boolean;
    releaseFiles: ReleaseFilesState;
}

interface UseProductVersionSelectionReturn {
    releaseFiles: ReleaseFilesState;
    selectedProductCodename: string;
    setSelectedProductCodename: (codename: string) => void;
    selectedVersion: string;
    setSelectedVersion: (version: string) => void;
    productOptions: SelectOption[];
    versionOptions: SelectOption[];
    selectedFile: API.ReleaseFile | undefined;
}

export const useEnrollmentProductSelection = (options: UseProductVersionSelectionOptions): UseProductVersionSelectionReturn => {
    const { autoSelect = true, releaseFiles } = options;
    const [selectedProductCodename, setSelectedProductCodename] = useState<string>("");
    const [selectedVersion, setSelectedVersion] = useState<string>("");

    const enrollmentReleaseFiles = useMemo(
        () => releaseFiles.data.filter((file) => enrollmentReleaseChannels.has(file.channel)),
        [releaseFiles.data],
    );

    const productOptions = useMemo(() => {
        const uniqueCodenames = [...new Set(enrollmentReleaseFiles.map((file) => file.codename))];
        return uniqueCodenames.map((codename) => {
            const firstFile = enrollmentReleaseFiles.find((file) => file.codename === codename);
            return {
                label: firstFile?.productName || codename,
                value: codename,
            };
        });
    }, [enrollmentReleaseFiles]);

    const versionOptions = useMemo(() => {
        if (!selectedProductCodename) {
            return [];
        }
        const versions = [
            ...new Set(
                enrollmentReleaseFiles.filter((file) => file.codename === selectedProductCodename).map((file) => file.version),
            ),
        ];
        return versions
            .sort((a, b) => SemverService.compare(b, a))
            .map((version) => ({
                label: version,
                value: version,
            }));
    }, [enrollmentReleaseFiles, selectedProductCodename]);

    const selectedFile = useMemo(() => {
        if (!selectedProductCodename || !selectedVersion) {
            return undefined;
        }
        return enrollmentReleaseFiles.find(
            (file) => file.codename === selectedProductCodename && file.version === selectedVersion,
        );
    }, [enrollmentReleaseFiles, selectedProductCodename, selectedVersion]);

    useEffect(() => {
        if (autoSelect && enrollmentReleaseFiles.length > 0 && !selectedProductCodename) {
            const uniqueCodenames = [...new Set(enrollmentReleaseFiles.map((file) => file.codename))];
            if (uniqueCodenames.length > 0) {
                setSelectedProductCodename(uniqueCodenames[0]);
                const versionsForProduct = [
                    ...new Set(
                        enrollmentReleaseFiles.filter((file) => file.codename === uniqueCodenames[0]).map((file) => file.version),
                    ),
                ];
                if (versionsForProduct.length > 0) {
                    setSelectedVersion(versionsForProduct[0]);
                }
            }
        }
    }, [enrollmentReleaseFiles, autoSelect, selectedProductCodename]);

    useEffect(() => {
        if (selectedProductCodename && versionOptions.length > 0) {
            setSelectedVersion(versionOptions[0].value);
        }
    }, [selectedProductCodename, versionOptions]);

    return {
        releaseFiles,
        selectedProductCodename,
        setSelectedProductCodename,
        selectedVersion,
        setSelectedVersion,
        productOptions,
        versionOptions,
        selectedFile,
    };
};
